<script setup lang="ts">
import type { CalculatedPrice } from '#shared/types/pricing'

const props = defineProps<{
  variant: PacketVariant
}>()

const auth = useAuthorizeStore()

const fixed = computed(() => !!props.variant.fixedAmount)

const totStudents = useTotStudents()

const prices = usePrices(props.variant)

const label = computed(() =>
  auth.isConsumer
    ? 'Lisensen varer i ett år.'
    : fixed.value
      ? 'Elevantall blir lagt til i handlekurven'
      : 'Antall legger du til i handlekurven',
)

const key = computed(() => (auth.isConsumer ? 'incVat' : 'exVat') satisfies keyof CalculatedPrice)
</script>

<template>
  <div v-if="prices !== 'NOT_APPLICABLE'" class="flex flex-col gap-s">
    <div class="flex flex-col gap-2xs">
      <div class="flex items-end justify-between">
        <h5
          class="text-lg font-semibold md:text-2xl"
          v-text="
            auth.isConsumer
              ? 'For privatpersoner'
              : fixed
                ? 'Til hele skolen'
                : 'Enkelttilganger'
          "
        />
        <IconsDiscount
          :value="prices.discounted?.percentage"
          size="small"
          class="[--background:var(--color-coral-20)] [--color:var(--color-black-80)] [--stroke:transparent]"
          :class="{
            invisible: !prices.discounted || prices.discounted.exVat === 0,
          }"
        />
      </div>
      <p class="text-base" v-text="label" />
    </div>
    <div class="flex items-center justify-between">
      <div class="flex flex-col gap-2xs">
        <template v-if="prices.discounted">
          <del
            aria-label="Ordinær pris per tilgang"
            class="text-base text-gray-40"
            v-text="norwegianPrice(prices.original[key])"
          />
          <PacketVariantCardActionPrice
            is="ins"
            :price="prices.discounted"
            :is-metered="prices.actual.isMetered"
            aria-label="Rabattert pris per tilgang"
            class="text-red-30 no-underline"
            is-discounted
          />
        </template>
        <template v-else>
          <PacketVariantCardActionPrice
            class="text-gray-50"
            :price="prices.original"
            :is-metered="prices.actual.isMetered"
            :is-discounted="false"
          />
        </template>
      </div>

      <AddToCartButton
        :variant="variant"
        :quantity="totStudents"
        :aria-label="`Bestill. ${label}`"
      >
        Bestill
      </AddToCartButton>
    </div>

    <DiscountDisclaimer
      :prices="prices"
      fallback="Gjelder kun første skoleår. Deretter gjelder vanlig/ordinær pris."
    />

    <PriceLoginCta
      :priceable="variant"
      singular-price
      class="text-base text-gray-40"
    />
  </div>
</template>
